import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/apiClient';
import useAuth from '../hooks/useAuth';
import Modal from '../components/Modal';

export default function TeacherDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from');
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [isZoomed, setIsZoomed] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [imgError, setImgError] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);

  // Edit Modal class assignment state
  const [editForm, setEditForm] = useState({ name: '', phone: '', email: '', branch_id: '' });
  const [editSelectedClassIds, setEditSelectedClassIds] = useState([]);
  const [editBranchClasses, setEditBranchClasses] = useState([]);
  const [editClassesLoading, setEditClassesLoading] = useState(false);
  const [allBranchesList, setAllBranchesList] = useState([]);

  useEffect(() => {
    loadAllBranches();
  }, []);

  async function loadAllBranches() {
    try {
      const res = await api.get('/admin/branches');
      setAllBranchesList(res.data.data || []);
    } catch (e) {}
  }

  useEffect(() => {
    if (isEditOpen && editForm.branch_id) {
      loadBranchClassesForEditModal(editForm.branch_id);
    } else {
      setEditBranchClasses([]);
    }
  }, [editForm.branch_id, isEditOpen]);

  async function loadBranchClassesForEditModal(branchId) {
    setEditClassesLoading(true);
    try {
      const res = await api.get(`/branches/${branchId}/classes`);
      setEditBranchClasses((res.data.data || []).filter(c => c.is_active === 1));
    } catch (e) {
      console.error(e);
    } finally {
      setEditClassesLoading(false);
    }
  }

  const openEditModal = () => {
    const currentTeacherBranches = data?.branches || [];
    const currentTeacherClasses = data?.classes || [];
    const primaryBranchId = currentTeacherBranches.length > 0 ? String(currentTeacherBranches[0].id) : '';

    setEditForm({
      name: teacher?.name || '',
      phone: teacher?.phone || '',
      email: teacher?.email || '',
      branch_id: primaryBranchId
    });
    setEditSelectedClassIds(currentTeacherClasses.map(c => c.id));
    setSelectedFile(null);
    setEditError('');
    setIsEditOpen(true);
  };

  const setFormVal = (key, val) => {
    setEditForm(prev => ({ ...prev, [key]: val }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setEditError('');
    try {
      const formData = new FormData();
      formData.append('name', editForm.name);
      formData.append('phone', editForm.phone);
      formData.append('email', editForm.email);
      if (editForm.branch_id) {
        formData.append('branch_id', editForm.branch_id);
      }
      formData.append('class_ids', JSON.stringify(editSelectedClassIds));

      if (selectedFile) {
        formData.append('profile_image', selectedFile);
      }

      await api.put(`/users/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      setIsEditOpen(false);
      load();
    } catch (err) {
      setEditError(err.response?.data?.message || 'Unable to update profile');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  async function load() {
    setImgError(false);
    try {
      const res = await api.get(`/admin/teachers/${id}`);
      setData(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load teacher');
    }
  }

  const updateStatus = async (activityId, status) => {
    try {
      await api.put(`/activities/${activityId}/status`, { status });
      setSelectedActivity(prev => (prev ? { ...prev, status } : null));
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update activity status');
    }
  };

  if (error) return <div className="error-message" style={{ margin: 24 }}>{error}</div>;
  if (!data) return <div style={{ padding: 32 }}>Loading...</div>;

  const { teacher, branches, classes = [], activities, announcements, stats } = data;
  const tabs = ['overview', 'activities', 'announcements'];

  const fileUrl = (p) => {
    if (!p) return null;
    let clean = String(p).trim();
    if (!clean || ['null', 'undefined', 'none', 'false', '0'].includes(clean.toLowerCase())) return null;
    if (clean.startsWith('http://') || clean.startsWith('https://')) return clean;

    const r2Url = process.env.REACT_APP_R2_PUBLIC_URL || 'https://pub-8403cb60474c4e039483cf5333503261.r2.dev';
    clean = clean.replace(/^\/+/, '').replace(/^uploads\//, '');
    if (!clean || ['null', 'undefined', 'none', 'false', '0'].includes(clean.toLowerCase())) return null;
    return `${r2Url}/${clean}`;
  };

  const resolvedImageUrl = fileUrl(teacher.profile_image);

  return (
    <div style={{ padding: 24 }}>

      <button
        onClick={() => {
          if (from === 'announcements') navigate('/announcements');
          else if (from === 'activities') navigate('/activities');
          else if (user?.role === 'center_manager') navigate('/staff');
          else navigate('/teachers');
        }}
        className="back-button"
      >
        {from === 'announcements'
          ? '← Back to Announcements'
          : from === 'activities'
          ? '← Back to Activities'
          : user?.role === 'center_manager'
          ? '← Back to Staff'
          : '← Back to Teachers'}
      </button>

      {/* Teacher Header */}
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, marginBottom: 20, border: '1px solid #e5e7eb', marginTop: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>

          {/* Profile Image / Avatar */}
          {resolvedImageUrl && !imgError ? (
            <img src={resolvedImageUrl} alt={teacher.name}
              onError={() => setImgError(true)}
              style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, cursor: 'pointer' }}
              onClick={() => setIsZoomed(true)} />
          ) : (
            <div style={{
              width: 72, height: 72, borderRadius: '50%', background: '#7c3aed',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 28, fontWeight: 700, flexShrink: 0
            }}>
              {teacher.name?.[0] || '?'}
            </div>
          )}

          <div>
            <h2 style={{ margin: 0, fontSize: 22 }}>{teacher.name}</h2>
            <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 14 }}>
              {teacher.phone} · {teacher.email}
            </p>
          </div>

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{
              padding: '4px 12px', borderRadius: 20, fontSize: 13,
              background: teacher.is_active ? '#d1fae5' : '#fee2e2',
              color: teacher.is_active ? '#065f46' : '#991b1b'
            }}>
              {teacher.is_active ? 'Active' : 'Inactive'}
            </span>
            <button
              onClick={openEditModal}
              className="primary-button"
              style={{ padding: '8px 14px', fontSize: 13 }}
            >
              Edit Profile
            </button>
          </div>
        </div>

        {/* Stats Row */}
        <div style={{ display: 'flex', gap: 24, marginTop: 20, paddingTop: 16, borderTop: '1px solid #f3f4f6' }}>
          {[
            ['Branches', stats.total_branches],
            ['Classes', (classes || []).length],
            ['Activities', stats.total_activities],
            ['Announcements', stats.total_announcements],
          ].map(([label, value]) => (
            <div key={label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#111827' }}>{value}</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {tabs.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontSize: 14,
            border: activeTab === tab ? 'none' : '1px solid #e5e7eb',
            background: activeTab === tab ? '#7c3aed' : '#fff',
            color: activeTab === tab ? '#fff' : '#374151',
            fontWeight: activeTab === tab ? 600 : 400,
            textTransform: 'capitalize'
          }}>
            {tab}
          </button>
        ))}
      </div>

      {/* Overview Tab — Branches & Assigned Classes */}
      {activeTab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
          {branches.length ? branches.map((b) => {
            const branchClasses = (classes || []).filter(c => c.branch_id === b.id);
            return (
              <div key={b.id} style={{
                background: '#fff', borderRadius: 12, padding: 20,
                border: `2px solid ${b.is_primary ? '#7c3aed' : '#e5e7eb'}`
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>{b.name}</div>
                    <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>{b.city}</div>
                    <div style={{ fontSize: 13, color: '#6b7280' }}>{b.address}</div>
                    <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>{b.branch_phone}</div>
                  </div>
                  {b.is_primary ? (
                    <span style={{
                      padding: '3px 10px', borderRadius: 12, fontSize: 12,
                      background: '#ede9fe', color: '#7c3aed', fontWeight: 600
                    }}>Primary</span>
                  ) : null}
                </div>

                {/* Assigned Classes */}
                <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px dashed #e2e8f0' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>🏫</span>
                    <span>Assigned Classes ({branchClasses.length}):</span>
                  </div>
                  {branchClasses.length > 0 ? (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {branchClasses.map(cls => (
                        <span key={cls.id} style={{
                          padding: '4px 10px',
                          borderRadius: 14,
                          fontSize: 12,
                          fontWeight: 600,
                          background: '#e0e7ff',
                          color: '#4338ca',
                          border: '1px solid #c7d2fe'
                        }}>
                          {cls.name} {cls.section ? `(${cls.section})` : ''}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
                      No classes assigned in this branch
                    </div>
                  )}
                </div>

                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 12 }}>
                  Assigned: {new Date(b.assigned_at).toLocaleDateString('en-IN')}
                </div>
              </div>
            );
          }) : <p style={{ color: '#9ca3af' }}>No branches assigned</p>}
        </div>
      )}

      {/* Activities Tab */}
      {activeTab === 'activities' && (
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb' }}>
          <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: '#1e293b' }}>Recent Activities</h3>
          {activities.length ? (
            <div className="table-container" style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', textAlign: 'left' }}>
                    <th style={{ padding: '10px 14px', fontWeight: 600 }}>Activity</th>
                    <th style={{ padding: '10px 14px', fontWeight: 600 }}>Category</th>
                    <th style={{ padding: '10px 14px', fontWeight: 600 }}>Branch</th>
                    <th style={{ padding: '10px 14px', fontWeight: 600 }}>Date & Time</th>
                    <th style={{ padding: '10px 14px', fontWeight: 600 }}>Status</th>
                    <th style={{ padding: '10px 14px', fontWeight: 600, textAlign: 'center' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {activities.map((a, i) => {
                    const formattedDate = a.activity_date ? String(a.activity_date).split('T')[0] : '-';
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '12px 14px' }}>
                          <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 14 }}>{a.title}</div>
                          {a.description && (
                            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {a.description}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{
                            padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                            background: '#ede9fe', color: '#7c3aed'
                          }}>{a.category}</span>
                        </td>
                        <td style={{ padding: '12px 14px', color: '#475569', fontWeight: 500 }}>
                          🏢 {a.branch_name || 'Branch'}
                        </td>
                        <td style={{ padding: '12px 14px', color: '#475569' }}>
                          <div style={{ fontWeight: 600, color: '#1e293b' }}>📅 {formattedDate}</div>
                          {a.activity_time && <div style={{ fontSize: 11, color: '#64748b' }}>⏰ {a.activity_time}</div>}
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{
                            padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, textTransform: 'capitalize',
                            background: a.status === 'approved' ? '#d1fae5' : a.status === 'rejected' ? '#fee2e2' : '#fef3c7',
                            color: a.status === 'approved' ? '#065f46' : a.status === 'rejected' ? '#991b1b' : '#92400e'
                          }}>{a.status || 'pending'}</span>
                        </td>
                        <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                          <button
                            type="button"
                            onClick={() => setSelectedActivity(a)}
                            style={{
                              padding: '6px 14px',
                              borderRadius: 6,
                              border: '1px solid #93c5fd',
                              background: '#eff6ff',
                              color: '#1d4ed8',
                              fontSize: 13,
                              fontWeight: 600,
                              cursor: 'pointer'
                            }}
                          >
                            Detail
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : <p style={{ color: '#9ca3af' }}>No activities recorded for this teacher.</p>}
        </div>
      )}

      {/* Announcements Tab */}
      {activeTab === 'announcements' && (
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb' }}>
          <h3 style={{ marginTop: 0, fontSize: 16 }}>Recent Announcements</h3>
          {announcements.length ? announcements.map((a, i) => (
            <div key={i} style={{ padding: '12px 0', borderBottom: '1px solid #f3f4f6' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{a.title}</span>
                    {a.is_pinned ? <span style={{ fontSize: 12 }}>📌</span> : null}
                    <span style={{
                      padding: '2px 8px', borderRadius: 10, fontSize: 11,
                      background: a.type === 'event' ? '#dbeafe' : a.type === 'reminder' ? '#fef3c7' : '#f3f4f6',
                      color: a.type === 'event' ? '#1e40af' : a.type === 'reminder' ? '#92400e' : '#374151'
                    }}>{a.type}</span>
                  </div>
                  <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>{a.body}</div>
                  <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                    📍 {a.branch_name} · {new Date(a.created_at).toLocaleDateString('en-IN')}
                  </div>
                </div>
              </div>
            </div>
          )) : <p style={{ color: '#9ca3af' }}>No announcements</p>}
        </div>
      )}

      {/* Image Zoom Modal */}
      {isZoomed && (
        <div 
          onClick={() => setIsZoomed(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            cursor: 'zoom-out'
          }}
        >
          <img 
            src={resolvedImageUrl} 
            alt={teacher.name}
            style={{ 
              maxWidth: '90%', 
              maxHeight: '90%', 
              borderRadius: '8px', 
              boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
              objectFit: 'contain'
            }} 
          />
          <button 
            onClick={(e) => { e.stopPropagation(); setIsZoomed(false); }}
            style={{
              position: 'absolute',
              top: 20,
              right: 20,
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '50%',
              width: 40,
              height: 40,
              color: '#fff',
              fontSize: 20,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Edit Profile Modal */}
      <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title="Edit Staff Profile">
        <form onSubmit={handleSave}>
          {editError && <div className="error-message" style={{ marginBottom: 12 }}>{editError}</div>}
          <label>
            Full Name
            <input 
              placeholder="Name" 
              value={editForm.name} 
              onChange={(e) => setFormVal('name', e.target.value)} 
              required 
            />
          </label>
          <label>
            Phone Number
            <input 
              placeholder="Phone" 
              value={editForm.phone} 
              onChange={(e) => setFormVal('phone', e.target.value)} 
            />
          </label>
          <label>
            Email Address
            <input 
              placeholder="Email" 
              type="email" 
              value={editForm.email} 
              onChange={(e) => setFormVal('email', e.target.value)} 
              required 
            />
          </label>
          <label style={{ display: 'block', marginBottom: '12px' }}>
            Branch
            <select
              value={editForm.branch_id || ''}
              onChange={(e) => setFormVal('branch_id', e.target.value)}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid #cbd5e1', fontSize: 14, marginTop: 4 }}
            >
              <option value="">Select Branch</option>
              {allBranchesList.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </label>

          {/* Classes Selection in Selected Branch */}
          {editForm.branch_id && (
            <div style={{ marginBottom: '16px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px' }}>
              <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '6px', color: '#334155' }}>
                Select Classes to Assign / Unassign (Multiple selection allowed)
              </div>
              {editClassesLoading ? (
                <div style={{ fontSize: '12px', color: '#64748b' }}>Loading branch classes...</div>
              ) : editBranchClasses.length === 0 ? (
                <div style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' }}>
                  No active classes found for this branch. Create classes from Branches page first.
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
                  gap: '6px',
                  maxHeight: '140px',
                  overflowY: 'auto',
                  marginTop: '6px'
                }}>
                  {editBranchClasses.map((cls) => {
                    const cid = Number(cls.id);
                    const isChecked = editSelectedClassIds.some(id => Number(id) === cid);
                    return (
                      <label
                        key={cls.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '12px',
                          fontWeight: isChecked ? 600 : 400,
                          color: isChecked ? '#4338ca' : '#475569',
                          cursor: 'pointer',
                          background: isChecked ? '#e0e7ff' : '#ffffff',
                          padding: '4px 8px',
                          borderRadius: '6px',
                          border: isChecked ? '1px solid #c7d2fe' : '1px solid #cbd5e1'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setEditSelectedClassIds(prev => Array.from(new Set([...prev.map(Number), cid])));
                            } else {
                              setEditSelectedClassIds(prev => prev.map(Number).filter(id => id !== cid));
                            }
                          }}
                        />
                        <span>{cls.name} {cls.section ? `(${cls.section})` : ''}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          <label>
            Profile Image
            <input 
              type="file" 
              accept="image/*" 
              onChange={(e) => setSelectedFile(e.target.files[0])} 
            />
          </label>
          <div className="modal-actions" style={{ marginTop: 20 }}>
            <button type="button" className="secondary-button" onClick={() => setIsEditOpen(false)} disabled={saving}>Cancel</button>
            <button type="submit" className="primary-button" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Activity Details Modal */}
      {selectedActivity && (
        <Modal isOpen={!!selectedActivity} onClose={() => setSelectedActivity(null)} title={`Activity Details #${selectedActivity.id}`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{
              background: '#faf5ff',
              padding: 16,
              borderRadius: 12,
              border: '1px solid #e9d5ff',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 18, color: '#3b0764' }}>{selectedActivity.title}</h3>
                <div style={{ fontSize: 13, color: '#6b21a8', marginTop: 2, fontWeight: 600 }}>
                  Category: {selectedActivity.category}
                </div>
              </div>
              {selectedActivity.status && (
                <span style={{
                  padding: '4px 12px',
                  borderRadius: 12,
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: 'capitalize',
                  background: selectedActivity.status === 'approved' ? '#d1fae5' : selectedActivity.status === 'rejected' ? '#fee2e2' : '#fef3c7',
                  color: selectedActivity.status === 'approved' ? '#065f46' : selectedActivity.status === 'rejected' ? '#991b1b' : '#92400e'
                }}>
                  {selectedActivity.status}
                </span>
              )}
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: 12,
              background: '#f8fafc',
              padding: 12,
              borderRadius: 10,
              border: '1px solid #e2e8f0',
              fontSize: 13
            }}>
              <div>
                <span style={{ color: '#64748b', fontSize: 11, display: 'block', fontWeight: 600 }}>Teacher</span>
                <strong style={{ color: '#1e293b' }}>{teacher?.name || '-'}</strong>
              </div>
              <div>
                <span style={{ color: '#64748b', fontSize: 11, display: 'block', fontWeight: 600 }}>Branch</span>
                <strong style={{ color: '#1e293b' }}>{selectedActivity.branch_name || '-'}</strong>
              </div>
              <div>
                <span style={{ color: '#64748b', fontSize: 11, display: 'block', fontWeight: 600 }}>Date</span>
                <strong style={{ color: '#1e293b' }}>{selectedActivity.activity_date ? String(selectedActivity.activity_date).split('T')[0] : '-'}</strong>
              </div>
              {selectedActivity.activity_time && (
                <div>
                  <span style={{ color: '#64748b', fontSize: 11, display: 'block', fontWeight: 600 }}>Time</span>
                  <strong style={{ color: '#1e293b' }}>{selectedActivity.activity_time}</strong>
                </div>
              )}
            </div>

            <div>
              <h4 style={{ margin: '0 0 6px', fontSize: 14, color: '#334155' }}>Description</h4>
              <div style={{
                background: '#f8fafc',
                padding: '12px 14px',
                borderRadius: 8,
                border: '1px solid #e2e8f0',
                fontSize: 13,
                color: '#334155',
                lineHeight: 1.5
              }}>
                {selectedActivity.description || <span style={{ color: '#94a3b8' }}>No description provided.</span>}
              </div>
            </div>

            {selectedActivity.students && selectedActivity.students.length > 0 && (
              <div>
                <h4 style={{ margin: '0 0 6px', fontSize: 14, color: '#334155' }}>Tagged Students</h4>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {selectedActivity.students.map(st => (
                    <span key={st.id} style={{
                      padding: '4px 10px',
                      background: '#eff6ff',
                      color: '#1d4ed8',
                      borderRadius: '16px',
                      fontSize: 13,
                      fontWeight: 500,
                      border: '1px solid #bfdbfe'
                    }}>
                      👤 {st.full_name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {selectedActivity.photos && selectedActivity.photos.length > 0 && (
              <div>
                <h4 style={{ margin: '0 0 8px', fontSize: 14, color: '#334155' }}>
                  Photos ({selectedActivity.photos.length})
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 10 }}>
                  {selectedActivity.photos.map((photoUrl, index) => (
                    <div key={index} style={{ aspectRatio: '1', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                      <img src={photoUrl} alt="Photo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="modal-actions" style={{ borderTop: '1px solid #e2e8f0', paddingTop: 16, marginTop: 8 }}>
              {selectedActivity.status !== 'approved' && (
                <button
                  type="button"
                  className="primary-button"
                  style={{ background: '#10b981', borderColor: '#10b981' }}
                  onClick={() => updateStatus(selectedActivity.id || selectedActivity.activity_id, 'approved')}
                >
                  Approve
                </button>
              )}
              {selectedActivity.status !== 'rejected' && (
                <button
                  type="button"
                  className="primary-button"
                  style={{ background: '#ef4444', borderColor: '#ef4444' }}
                  onClick={() => updateStatus(selectedActivity.id || selectedActivity.activity_id, 'rejected')}
                >
                  Reject
                </button>
              )}
              <button
                type="button"
                className="secondary-button"
                onClick={() => setSelectedActivity(null)}
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
